// redux/validation/validationResultsSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { generateMockEventData } from '../../utilities/mock/mockEventService';
import { compareEventWithFlow } from '../../utilities/validation/eventComparisonService';
import { fetchFlowWithDetailsAsync } from '../flows/getFlowsSlice';
import { fetchEventsForValidation } from '../../services/eventApiService';
import axios from '../../services/authService';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { parseApiResponse } from '../../utilities/parseApiResponse';

// ========================================
// ASYNC THUNKS
// ========================================

// Async thunk to fetch events from the API
export const fetchEventsAsync = createAsyncThunk(
  'validation/fetchEvents',
  async ({ flowDbId, deviceId, marketId, timestampStart, timestampEnd }) => {
    const events = await fetchEventsForValidation(
      flowDbId,
      deviceId,
      marketId,
      timestampStart,
      timestampEnd,
    );
    return events;
  },
);

// Async thunk to add extra event to test case
export const addExtraEventToTestCaseAsync = createAsyncThunk(
  'validation/addExtraEventToTestCase',
  async ({ eventIndex, options }, { getState, dispatch }) => {
    const state = getState();
    const metadata = state.validationResults.metadata;
    const event = state.validationResults.value[eventIndex];
    const flowsById = state.getFlows.flowsById;
    const currentFlow = flowsById[metadata.flow_db_id];

    if (!metadata || !event || !currentFlow) {
      throw new Error('Missing required data');
    }

    const {
      isInbound,
      createNewEvent,
      existingEventId,
      existingScenarioId,
      eventName,
      beaconType,
      attributes,
      attributesToDelete = [],
    } = options;

    let eventId = existingEventId;
    let newEventOrder = null;

    // Map beaconType to outbound_type_id
    const getOutboundTypeId = (type) => {
      switch (type.toLowerCase()) {
        case 'epsilon':
          return 3;
        case 'kochava':
          return 2;
        case 'mixpanel':
          return 1;
        default:
          return 1;
      }
    };

    // Step 1: Create new event if needed
    if (createNewEvent) {
      // Calculate the last event order
      const maxOrder = currentFlow.events.reduce((max, evt) => {
        return Math.max(max, evt.event_order || 0);
      }, 0);
      newEventOrder = maxOrder + 1;

      const eventPayload = [
        {
          section_data: 'test-events',
          test_flow_id: metadata.flow_db_id,
          event_name: eventName,
          sequence: newEventOrder,
          description: '',
        },
      ];

      const eventResponse = await axios.post(API_ENDPOINTS.INSERT_TEST_FLOW, eventPayload);
      const parsedBody = parseApiResponse(eventResponse);

      if (parsedBody?.resDataObject?.test_events?.length > 0) {
        eventId = parsedBody.resDataObject.test_events[0];
      } else {
        throw new Error('Failed to create event');
      }
    }

    // Step 2: Create scenario ONLY if we don't have an existing one
    let scenarioId = existingScenarioId; // May already have a scenario ID from user selection

    if (!scenarioId) {
      const scenarioType = isInbound ? 'inbound' : `outbound-${beaconType.toLowerCase()}`;
      const sectionData = isInbound ? 'test-events-inbound' : 'test-events-outbound';

      const scenarioPayload = isInbound
        ? [
            {
              section_data: sectionData,
              test_event_id: eventId,
              scenario_name: eventName,
              scenario_type: scenarioType,
              description: '',
              matching_attribute_name: null,
              matching_attribute_value: null,
            },
          ]
        : [
            {
              section_data: sectionData,
              test_event_id: eventId,
              scenario_name: eventName,
              scenario_type: scenarioType,
              outbound_type_id: getOutboundTypeId(beaconType),
              description: '',
              matching_attribute_name: null,
              matching_attribute_value: null,
            },
          ];

      const scenarioResponse = await axios.post(API_ENDPOINTS.INSERT_TEST_FLOW, scenarioPayload);
      const scenarioParsedBody = parseApiResponse(scenarioResponse);

      const scenarioIds =
        scenarioParsedBody?.resDataObject?.[sectionData] ||
        scenarioParsedBody?.resDataObject?.[sectionData.replace(/-/g, '_')];
      if (scenarioIds?.length > 0) {
        scenarioId = scenarioIds[0];
      } else {
        throw new Error('Failed to create scenario');
      }
    }

    // Step 2.5: Delete attributes being overwritten (if any)
    if (attributesToDelete.length > 0) {
      const scenarioType = isInbound ? 'inbound' : 'outbound';
      const paramName = scenarioType === 'inbound' ? 'inbound_id' : 'outbound_id';

      // Delete each attribute
      for (const attr of attributesToDelete) {
        const url = `${API_ENDPOINTS.DELETE_ATTRIBUTE}?${paramName}=${attr.id}`;
        await axios.delete(url);
      }
    }

    // Step 3: Create attributes
    const attributePayloads = attributes.map((attr) => {
      const payload = {
        section_data: isInbound ? 'test-event-inbound-details' : 'test-event-outbound-details',
        name: attr.name,
        expected: String(attr.value),
        config_type: 'Static',
      };

      if (isInbound) {
        payload.test_events_inbound_id = scenarioId;
      } else {
        payload.test_events_outbound_id = scenarioId;
      }

      return payload;
    });

    if (attributePayloads.length > 0) {
      await axios.post(API_ENDPOINTS.INSERT_TEST_FLOW, attributePayloads);
    }

    // Refresh flow cache
    await dispatch(fetchFlowWithDetailsAsync(metadata.flow_db_id));

    return {
      eventIndex,
      eventId,
      scenarioId,
      createdNewEvent: createNewEvent,
      usedExistingScenario: !!existingScenarioId,
    };
  },
);

// ========================================
// REDUX SLICE
// ========================================

export const validationResultsSlice = createSlice({
  name: 'validationResults',
  initialState: {
    value: [],
    loading: false,
    includeNonValidated: true,
    includeOutboundBeacons: false,
    metadata: null,
    addedEvents: {}, // Track which events have been added { eventIndex: true }
  },
  reducers: {
    updateOutput: (state, action) => {
      state.loading = false;
      // Defensive check: ensure payload exists and is an array
      if (!action.payload || !Array.isArray(action.payload)) {
        console.error('updateOutput: payload is not an array', action.payload);
        state.value = [];
        return;
      }
      state.value = action.payload.map((item) => ({
        beaconID: item.beaconID,
        index: item.index,
        timestamp: item.timestamp,
        beaconType: item.beaconType || 'inbound',
        deviceID: item.deviceID,
        truth: item.truth || { data: {}, status: 404 },
        beaconData: item.beaconData || { data: {}, status: 404 },
        modifiedRows: item.modifiedRows || {},
        notes: item.notes || '',
        manualBeaconStatus: item.manualBeaconStatus || '',
        flowComparison: item.flowComparison || null,
        eventName: item.eventName,
        isMissing: item.isMissing || false,
        hasOrderIssue: item.hasOrderIssue || false,
        flowHasOrderIssue: item.flowHasOrderIssue || false,
        actualSequence: item.actualSequence,
        stepName: item.stepName,
        stepNumber: item.stepNumber,
        isBlockedEvent: item.isBlockedEvent || false,
        blockedUserAttributes: item.blockedUserAttributes || null,
      }));
    },

    reset: (state) => {
      state.value = [];
      state.metadata = null;
      state.addedEvents = {};
    },
    markEventAsAdded: (state, action) => {
      const { eventIndex } = action.payload;
      state.addedEvents[eventIndex] = true;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setMetadata: (state, action) => {
      state.metadata = action.payload;
    },
    setRequestStatus: (state, action) => {
      state.requestStatus = action.payload;
    },
    setRequestMessage: (state, action) => {
      state.requestMessage = action.payload;
    },
    updateModifiedRow: (state, action) => {
      const { beaconIndex, variableName, newStatus } = action.payload;
      const beacon = state.value.find((b) => b.index === beaconIndex);
      if (beacon) {
        beacon.modifiedRows = {
          ...beacon.modifiedRows,
          [variableName]: newStatus,
        };
      }
    },
    updateNotes: (state, action) => {
      const { beaconIndex, notes } = action.payload;
      const beacon = state.value.find((b) => b.index === beaconIndex);
      if (beacon) {
        beacon.notes = notes;
      }
    },
    updateManualBeaconStatus: (state, action) => {
      const { beaconIndex, newStatus } = action.payload;
      const beacon = state.value.find((b) => b.index === beaconIndex);
      if (beacon) {
        beacon.manualBeaconStatus = newStatus;
      }
    },
    setIncludeNonValidated: (state, action) => {
      state.includeNonValidated = action.payload;
    },
    setIncludeOutboundBeacons: (state, action) => {
      state.includeOutboundBeacons = action.payload;
    },
  },
});

export const {
  updateOutput,
  reset,
  setLoading,
  setMetadata,
  setRequestStatus,
  setRequestMessage,
  updateModifiedRow,
  updateNotes,
  updateManualBeaconStatus,
  setIncludeNonValidated,
  setIncludeOutboundBeacons,
  markEventAsAdded,
} = validationResultsSlice.actions;

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Builds the expected scenario queue from flow events
 * @param {Object} flowData - The flow data containing events and scenarios
 * @returns {Array} Array of expected queue items with scenario, parent event, and type info
 */
function buildExpectedQueue(flowData) {
  const expectedQueue = [];
  const sortedFlowEvents = [...flowData.events].sort(
    (a, b) => (a.event_order || 0) - (b.event_order || 0),
  );

  sortedFlowEvents.forEach((event, eventIndex) => {
    event.scenarios?.forEach((scenario) => {
      expectedQueue.push({
        scenario: scenario,
        parentEvent: event,
        position: event.event_order !== undefined ? event.event_order : eventIndex + 1,
        scenarioType: scenario.scenario_type || 'inbound',
      });
    });
  });

  return expectedQueue;
}

/**
 * Flattens nested event data into a single-level object
 * @param {Object} eventData - The nested event data to flatten
 * @returns {Object} Flattened event data with dot notation keys
 *
 * Note: This function handles both internal metadata and display attributes:
 * - Internal metadata (event_name, event_direction, timestamp, etc.) - EXCLUDED from flattened output
 * - Top-level event_object properties (event_type from mParticle) - flattened WITHOUT prefix
 * - Nested data properties (data.custom_attributes.screen) - flattened WITH "data." prefix
 */
function flattenEventData(eventData) {
  const flattenedEventData = {};

  // Define internal metadata fields (never display in tables)
  const INTERNAL_METADATA = [
    'event_name',
    'event_direction',
    'timestamp',
    'device_id',
    'is_blocked_event',
    'data', // We handle this separately below
  ];

  const flattenData = (obj, prefix = 'data') => {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (value === null) {
        flattenedEventData[fullKey] = null;
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        flattenData(value, fullKey);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item === null) {
            flattenedEventData[`${fullKey}[${index}]`] = null;
          } else if (item && typeof item === 'object') {
            flattenData(item, `${fullKey}[${index}]`);
          } else {
            flattenedEventData[`${fullKey}[${index}]`] = item;
          }
        });
      } else {
        flattenedEventData[fullKey] = value;
      }
    });
  };

  // Add top-level event_object properties WITHOUT prefix (e.g., event_type from mParticle)
  // Exclude internal metadata
  // Add these FIRST so they appear before data.* keys in Object.keys() order
  Object.keys(eventData).forEach((key) => {
    if (!INTERNAL_METADATA.includes(key)) {
      flattenedEventData[key] = eventData[key];
    }
  });

  // Flatten the nested data object with "data." prefix
  // Add these SECOND so data.* keys appear after top-level keys
  if (eventData.data) {
    flattenData(eventData.data);
  }

  return flattenedEventData;
}

/**
 * Determines beacon type from event direction string
 * @param {string} eventDirection - The event direction string (e.g., "inbound", "outbound-epsilon")
 * @returns {string} The beacon type (e.g., "epsilon", "inbound")
 */
function determineBeaconType(eventDirection) {
  if (!eventDirection || eventDirection === 'inbound') {
    return 'inbound';
  }
  if (eventDirection.startsWith('outbound-')) {
    return eventDirection.replace('outbound-', '');
  }
  if (eventDirection === 'outbound') {
    return 'outbound';
  }
  return 'inbound';
}

/**
 * Performs lookahead matching to find first unconsumed matching scenario
 * @param {Object} apiEvent - The API event to match
 * @param {Array} expectedQueue - The queue of expected scenarios
 * @param {Set} consumedPositions - Set of already consumed queue positions
 * @returns {Object} Match result with position, scenario, and validation status
 */
function performLookaheadMatching(apiEvent, expectedQueue, consumedPositions) {
  const normalizedEventName = apiEvent.event_name?.trim() || '';
  const actualEventIsInbound = !apiEvent.event_direction || apiEvent.event_direction === 'inbound';

  // Step 1: Build list of candidates (matching event_name AND event_direction)
  const candidates = [];

  for (let i = 0; i < expectedQueue.length; i++) {
    if (consumedPositions.has(i)) {
      continue; // Skip already consumed positions
    }

    const expected = expectedQueue[i];
    const expectedScenario = expected.scenario;
    const normalizedScenarioName = expectedScenario.scenario_name?.trim() || '';

    // Compare event names (case-insensitive)
    const nameMatch =
      normalizedEventName === normalizedScenarioName ||
      normalizedEventName.toLowerCase() === normalizedScenarioName.toLowerCase();

    // Check event TYPE (inbound events should only match inbound scenarios)
    const expectedScenarioIsInbound = expected.scenarioType === 'inbound';
    const typeMatch = actualEventIsInbound === expectedScenarioIsInbound;

    if (nameMatch && typeMatch) {
      candidates.push({ expected, queueIndex: i });
    }
  }

  // If no candidates found, return no match
  if (candidates.length === 0) {
    return {
      matched: false,
      queueIndex: -1,
      scenario: null,
      parentEvent: null,
      scenarioType: null,
      position: null,
    };
  }

  // Step 2: Try to find a SPECIFIC match (scenarios WITH match key/value)
  for (const candidate of candidates) {
    const expectedScenario = candidate.expected.scenario;

    // Check if scenario has match requirements
    if (expectedScenario.matching_attribute_name && expectedScenario.matching_attribute_value) {
      // Get the actual value from the event at the match key path
      const eventValue =
        apiEvent.data?.custom_attributes?.[expectedScenario.matching_attribute_name];

      // Check if it matches the expected value
      if (eventValue === expectedScenario.matching_attribute_value) {
        // Found specific match!
        return {
          matched: true,
          queueIndex: candidate.queueIndex,
          scenario: expectedScenario,
          parentEvent: candidate.expected.parentEvent,
          scenarioType: candidate.expected.scenarioType,
          position: candidate.expected.position,
        };
      }
    }
  }

  // Step 3: Fall back to GENERIC match (scenarios WITHOUT match key/value)
  for (const candidate of candidates) {
    const expectedScenario = candidate.expected.scenario;

    // Check if scenario has NO match requirements (generic scenario)
    if (!expectedScenario.matching_attribute_name && !expectedScenario.matching_attribute_value) {
      // Found generic match!
      return {
        matched: true,
        queueIndex: candidate.queueIndex,
        scenario: expectedScenario,
        parentEvent: candidate.expected.parentEvent,
        scenarioType: candidate.expected.scenarioType,
        position: candidate.expected.position,
      };
    }
  }

  // No match found (all candidates had match requirements but none matched)
  return {
    matched: false,
    queueIndex: -1,
    scenario: null,
    parentEvent: null,
    scenarioType: null,
    position: null,
  };
}

/**
 * Creates beacon data structures for validation results
 * @param {Object} apiEvent - The API event
 * @param {Object} matchingScenario - Matching scenario from flow
 * @param {Object} matchingEvent - Parent event from flow
 * @param {Object} flowData - Flow data for context
 * @returns {Object} Object containing beaconData, truthData, and flowComparison
 */
function createBeaconDataStructures(apiEvent, matchingScenario, matchingEvent, flowData) {
  let beaconData, truthData, flowComparison;

  // Build flattened proxy data if available
  let flattenedProxyData = null;
  if (apiEvent.proxyData) {
    // Build a pseudo-event with just the proxy data to reuse flattenEventData
    const proxyPseudoEvent = { ...apiEvent, data: apiEvent.proxyData };
    flattenedProxyData = flattenEventData(proxyPseudoEvent);
  }

  if (matchingScenario) {
    // Create truth data from flow expectations
    const expectedAttributes = buildExpectedAttributes(matchingScenario);

    truthData = {
      data: expectedAttributes,
      status: 200,
      config: { url: `flow:///${flowData.flow_name}/${matchingEvent.event_name}` },
    };

    // Create beacon data from API event
    const flattenedEventData = flattenEventData(apiEvent);

    beaconData = {
      data: flattenedEventData,
      proxyData: flattenedProxyData,
      status: 200,
      config: { url: `mock://events/${apiEvent.event_name}` },
    };

    // Generate comparison results (pass flattened proxy for display)
    flowComparison = compareEventWithFlow(apiEvent, matchingScenario, flattenedProxyData);
  } else {
    // Event not found in flow - mark as "Not Validated"
    const flattenedEventData = flattenEventData(apiEvent);

    beaconData = {
      data: flattenedEventData,
      proxyData: flattenedProxyData,
      status: 200,
      config: { url: `mock://events/${apiEvent.event_name}` },
    };

    truthData = {
      data: {},
      status: 404,
      config: { url: `flow:///${flowData.flow_name}/not-found` },
    };

    flowComparison = null;
  }

  return { beaconData, truthData, flowComparison };
}

/**
 * Calculates order issues for inbound events in second pass
 * @param {Array} processedBeacons - Array of processed beacon objects
 * @param {Array} expectedQueue - The expected scenario queue
 * @param {Set} consumedPositions - Set of consumed queue positions
 */
function calculateOrderIssues(processedBeacons, expectedQueue, consumedPositions) {
  console.log(`🔍 SECOND PASS: Calculating order issues excluding missing positions`);

  // Get all inbound beacons that consumed a queue position, sorted by arrival time
  const inboundBeaconsInArrivalOrder = processedBeacons
    .filter((b) => b.consumedQueuePosition !== undefined && b.consumedQueuePosition !== -1)
    .filter((b) => {
      const expected = expectedQueue[b.consumedQueuePosition];
      return expected && expected.scenarioType === 'inbound';
    })
    .sort((a, b) => a.timestamp - b.timestamp);

  inboundBeaconsInArrivalOrder.forEach((beacon, arrivalIndex) => {
    const queuePos = beacon.consumedQueuePosition;

    // Count how many CONSUMED inbound positions exist before queuePos (excluding missing)
    const consumedInboundBefore = expectedQueue
      .slice(0, queuePos)
      .filter((q, idx) => q.scenarioType === 'inbound' && consumedPositions.has(idx)).length;

    // This is the (arrivalIndex + 1)th inbound event to arrive
    // It should have consumed a position with exactly arrivalIndex consumed inbound positions before it
    if (consumedInboundBefore === arrivalIndex) {
      beacon.hasOrderIssue = false;
      console.log(
        `   ✅ Event ${arrivalIndex + 1} "${beacon.eventName}" consumed position ${queuePos} with ${consumedInboundBefore} consumed inbound before it - IN ORDER`,
      );
    } else {
      beacon.hasOrderIssue = true;
      console.log(
        `   ❌ Event ${arrivalIndex + 1} "${beacon.eventName}" consumed position ${queuePos} with ${consumedInboundBefore} consumed inbound before it (expected ${arrivalIndex}) - OUT OF ORDER`,
      );
    }
  });
}

/**
 * Builds expected attributes object from scenario attributes
 * @param {Object} scenario - Scenario containing attributes
 * @returns {Object} Expected attributes object
 */
function buildExpectedAttributes(scenario) {
  const expectedAttributes = {};
  if (scenario?.scenario_attributes) {
    scenario.scenario_attributes.forEach((attr) => {
      expectedAttributes[attr.attribute_name] = {
        value: attr.expected_value,
        fieldType: attr.attribute_type,
      };
    });
  }
  return expectedAttributes;
}

/**
 * Builds expected order for inbound events
 * @param {Object} flowData - Flow data containing events
 * @returns {Array} Sorted array of expected event order
 */
function buildExpectedInboundOrder(flowData) {
  const expectedEventOrder = [];
  flowData.events.forEach((event, eventIndex) => {
    event.scenarios
      .filter((scenario) => scenario.scenario_type === 'inbound')
      .forEach((scenario) => {
        const eventOrder = event.event_order !== undefined ? event.event_order : eventIndex;
        expectedEventOrder.push({
          eventName: scenario.scenario_name,
          stepOrder: eventOrder,
          stepName: event.event_name,
        });
      });
  });
  return [...expectedEventOrder].sort((a, b) => a.stepOrder - b.stepOrder);
}

/**
 * Categorizes processed beacons into validated/non-validated groups
 * @param {Array} processedBeacons - Array of processed beacons
 * @returns {Object} Categorized beacons
 */
function categorizeProcessedEvents(processedBeacons) {
  return {
    validatedInbound: processedBeacons.filter(
      (b) => !b.isMissing && b.flowComparison && b.beaconType === 'inbound',
    ),
    validatedOutbound: processedBeacons.filter(
      (b) => !b.isMissing && b.flowComparison && b.beaconType !== 'inbound',
    ),
    nonValidated: processedBeacons.filter((b) => !b.isMissing && !b.flowComparison),
  };
}

/**
 * Creates missing inbound events for second pass
 * @param {Array} expectedInboundEvents - Expected inbound events from flow
 * @param {Array} processedBeacons - Already processed beacons
 * @param {Object} flowData - Flow data
 * @param {string} deviceID - Device ID
 * @param {number} startIndex - Starting index for beacon IDs
 * @returns {Array} Array of missing event beacons
 */
function createMissingInboundEvents(
  expectedInboundEvents,
  processedBeacons,
  flowData,
  deviceID,
  startIndex,
  expectedQueue,
  consumedPositions,
) {
  const missingEvents = [];
  let index = startIndex;

  // Iterate through expectedQueue and check which positions were never consumed
  for (let i = 0; i < expectedQueue.length; i++) {
    if (!consumedPositions.has(i)) {
      // This queue position was never consumed - it's missing
      const expected = expectedQueue[i];
      const expectedScenario = expected.scenario;
      const scenarioType = expected.scenarioType;

      // Process BOTH inbound and outbound missing scenarios
      if (scenarioType === 'inbound') {
        const expectedAttributes = buildExpectedAttributes(expectedScenario);
        const matchingEvent = expected.parentEvent;
        const slotNumber = expected.position;

        const missingBeacon = {
          beaconID: `missing-${expectedScenario.scenario_name}-${i}`,
          index: index++,
          timestamp: -(slotNumber * 1000 + i), // Negative to ensure proper sorting, unique per queue position
          beaconType: 'inbound',
          deviceID: deviceID || 'unknown',
          truth: {
            data: expectedAttributes,
            status: 200,
            config: { url: `flow:///${flowData.flow_name}/${expectedScenario.scenario_name}` },
          },
          beaconData: {
            data: {},
            status: 404,
            config: { url: 'mock://events/missing' },
          },
          flowComparison: null,
          eventName: expectedScenario.scenario_name,
          stepName: matchingEvent ? matchingEvent.event_name : `Event ${slotNumber}`,
          stepNumber: slotNumber,
          consumedQueuePosition: i, // Track which queue position this was
          isMissing: true,
          actualSequence: undefined,
          hasOrderIssue: false,
          expectedPosition: slotNumber,
        };

        missingEvents.push(missingBeacon);
      } else {
        // Handle missing outbound scenarios
        const outboundType = determineBeaconType(scenarioType);
        const outboundExpectedAttributes = buildExpectedAttributes(expectedScenario);
        const matchingEvent = expected.parentEvent;
        const slotNumber = expected.position;

        const missingOutboundBeacon = {
          beaconID: `missing-${expectedScenario.scenario_name}-${i}`,
          index: index++,
          timestamp: -(slotNumber * 1000 + i + 0.001), // Slightly after inbound, unique per queue position
          beaconType: outboundType,
          deviceID: deviceID || 'unknown',
          truth: {
            data: outboundExpectedAttributes,
            status: 200,
            config: { url: `flow:///${flowData.flow_name}/${expectedScenario.scenario_name}` },
          },
          beaconData: {
            data: {},
            status: 404,
            config: { url: 'mock://events/missing' },
          },
          flowComparison: null,
          eventName: expectedScenario.scenario_name,
          stepName: matchingEvent ? matchingEvent.event_name : `Event ${slotNumber}`,
          stepNumber: slotNumber,
          consumedQueuePosition: i, // Track which queue position this was
          isMissing: true,
          actualSequence: undefined,
          hasOrderIssue: false,
          expectedPosition: slotNumber,
        };

        missingEvents.push(missingOutboundBeacon);
      }
    }
  }

  return missingEvents;
}

/**
 * Assembles the final event list by merging existing and missing events
 * @param {Array} allExistingEvents - All existing events sorted by timestamp
 * @param {Array} missingEvents - Missing event beacons to insert
 * @param {Array} expectedQueue - Expected queue for reference (not used, kept for compatibility)
 * @param {Set} consumedPositions - Set of consumed positions (not used, kept for compatibility)
 * @returns {Array} Final merged list of all events
 */
function assembleFinalEventList(
  allExistingEvents,
  missingEvents,
  expectedQueue,
  consumedPositions,
) {
  // Start with existing events (already sorted by timestamp)
  let finalList = [...allExistingEvents];

  // Insert each missing event at its logical queue position
  missingEvents.forEach((missingEvent) => {
    const missingQueuePos = missingEvent.consumedQueuePosition;

    // Find insertion index based on queue positions
    let insertIndex = 0;

    // Find the last event with a valid consumedQueuePosition < missingQueuePos
    // This ensures missing events are inserted in their logical sequence position
    // while preserving timestamp order for actual events
    for (let i = finalList.length - 1; i >= 0; i--) {
      const event = finalList[i];

      // Only consider events that consumed a queue position (not extra events)
      if (
        event.consumedQueuePosition !== undefined &&
        event.consumedQueuePosition !== -1 &&
        event.consumedQueuePosition < missingQueuePos
      ) {
        insertIndex = i + 1; // Insert after this event
        break;
      }
    }

    // Insert the missing event at the calculated position
    finalList.splice(insertIndex, 0, missingEvent);
  });

  return finalList;
}

// ========================================
// MAIN VALIDATION FUNCTION
// ========================================

/**
 * Main async function for beacon validation
 * Implements two-pass lookahead validation with order checking
 */
export const validateEventsAsync =
  (
    testCaseId,
    deviceID,
    includeOutbound = false,
    timestampStart,
    timestampEnd,
    marketId,
    flowDbId,
    metadata = null,
  ) =>
  async (dispatch, getState) => {
    dispatch(setLoading(true));
    dispatch(reset());
    dispatch(setIncludeOutboundBeacons(includeOutbound));

    // Store metadata if provided
    if (metadata) {
      dispatch(setMetadata(metadata));
    }

    let requestStatus = 200;
    let requestMessage = '';

    try {
      // ========================================
      // PHASE 1: VALIDATION & DATA RETRIEVAL
      // ========================================

      // Validate that a test case is selected
      if (!testCaseId || testCaseId === 'None') {
        requestStatus = 400;
        requestMessage = 'Please select a test case before submitting';
        throw new Error('No test case selected');
      }

      // Get flow data from Redux state
      const state = getState();
      // Filter flows by market first to ensure we get the correct flow for this market
      const flowsByMarket = state.getFlows.flows.filter((f) => f.market_id === marketId);
      let flowData = flowsByMarket.find((f) => f.test_case_id === testCaseId);

      if (!flowData) {
        requestStatus = 404;
        requestMessage = `Test case "${testCaseId}" not found`;
        throw new Error('Test case not found');
      }

      // Check if this is the mock test case
      const isMockTestCase = flowData.test_case_id === 'TC-MOCK-004';

      // If events aren't loaded yet, fetch flow details from database
      if (!flowData.events || flowData.events.length === 0) {
        const flowDbId = flowData.flow_db_id || flowData.flow_id;
        const cachedFlow = state.getFlows.flowsById[flowDbId];
        if (cachedFlow && cachedFlow.events && cachedFlow.events.length > 0) {
          flowData = cachedFlow;
        } else {
          try {
            const result = await dispatch(fetchFlowWithDetailsAsync(flowDbId));
            if (result && result.payload) {
              flowData = result.payload;
            } else {
              requestStatus = 404;
              requestMessage = 'Failed to load flow details from database';
              throw new Error('Failed to load flow details');
            }
          } catch (fetchError) {
            requestStatus = 500;
            requestMessage = 'Error fetching flow details: ' + fetchError.message;
            throw new Error('Error fetching flow details');
          }
        }
      }

      // ========================================
      // PHASE 2: FETCH/GENERATE EVENTS
      // ========================================

      let apiEvents;

      if (isMockTestCase) {
        // Use existing mock generation for TC-MOCK-004
        apiEvents = generateMockEventData(flowData.flow_name, deviceID);

        if (apiEvents.length === 0) {
          requestStatus = 501;
          requestMessage = 'No mock events were generated for TC-MOCK-004';
          throw new Error(requestMessage);
        }
      } else {
        // Use real API for all other flows
        try {
          const result = await dispatch(
            fetchEventsAsync({
              flowDbId: flowDbId,
              deviceId: deviceID,
              marketId: marketId,
              timestampStart: timestampStart,
              timestampEnd: timestampEnd,
            }),
          );

          apiEvents = result.payload || [];

          if (apiEvents.length === 0) {
            requestStatus = 404;
            requestMessage = 'No events found for the specified time range and parameters';
            throw new Error(requestMessage);
          }
        } catch (fetchError) {
          requestStatus = 500;
          requestMessage = 'Error fetching events from API: ' + fetchError.message;
          throw new Error(requestMessage);
        }
      }

      // Sort events by timestamp
      apiEvents.sort((a, b) => a.timestamp - b.timestamp);

      // ========================================
      // PHASE 3: BUILD EXPECTED QUEUE
      // ========================================

      const expectedQueue = buildExpectedQueue(flowData);

      // Debug logging
      console.log('🔍 VALIDATION DEBUG - Flow Data:', {
        testCaseId: flowData.test_case_id,
        flowName: flowData.flow_name,
        numberOfEvents: flowData.events?.length || 0,
        events: flowData.events,
      });

      console.log('🔍 VALIDATION DEBUG - API Events to Validate:', {
        count: apiEvents.length,
        eventNames: apiEvents.map((e) => e.event_name),
        fullEvents: apiEvents,
      });

      // Extract scenario names for debug logging
      const allScenarioNames = flowData.events?.flatMap(
        (event) => event.scenarios?.map((scenario) => scenario.scenario_name) || [],
      );
      console.log('🔍 VALIDATION DEBUG - All Scenario Names in Flow:', allScenarioNames);
      console.log(
        '🔍 SEQUENTIAL VALIDATION - Expected Queue:',
        expectedQueue.map((eq, idx) => ({
          position: idx + 1,
          scenarioName: eq.scenario.scenario_name,
          scenarioType: eq.scenarioType,
          parentEvent: eq.parentEvent.event_name,
        })),
      );

      // ========================================
      // PHASE 4: FIRST PASS - LOOKAHEAD MATCHING
      // ========================================

      const consumedPositions = new Set();
      let inboundConsumedCount = 0;
      const processedBeacons = [];
      let index = 0;

      apiEvents.forEach((apiEvent) => {
        console.log(
          `🔍 LOOKAHEAD: Processing event #${index + 1}: "${apiEvent.event_name}" (timestamp: ${apiEvent.timestamp})`,
        );
        console.log(`   Inbound events consumed so far: ${inboundConsumedCount}`);

        // Perform lookahead matching
        const matchResult = performLookaheadMatching(apiEvent, expectedQueue, consumedPositions);

        let sequentialResult = 'NOT_VALIDATED';

        if (matchResult.matched) {
          sequentialResult = 'PASS'; // Tentative, may become FAIL if attributes don't match

          console.log(
            `   ✅ Found match at queue position ${matchResult.queueIndex} (scenario: "${matchResult.scenario.scenario_name}")`,
          );

          // Mark this position as consumed
          consumedPositions.add(matchResult.queueIndex);
          console.log(`   ✔️  Consumed queue position ${matchResult.queueIndex}`);

          // Track inbound consumption
          if (matchResult.scenarioType === 'inbound') {
            inboundConsumedCount++;
            console.log(`   Inbound consumed count: ${inboundConsumedCount}`);
          }
        } else {
          console.log(
            `   ⚠️  NOT VALIDATED: No unconsumed queue position matches "${apiEvent.event_name}"`,
          );
        }

        // Create beacon data structures
        const { beaconData, truthData, flowComparison } = createBeaconDataStructures(
          apiEvent,
          matchResult.scenario,
          matchResult.parentEvent,
          flowData,
        );

        // Check if attributes passed
        if (matchResult.matched && flowComparison && Array.isArray(flowComparison)) {
          const hasAttributeFailure = flowComparison.some(
            (attr) => attr.status === 'fail' || attr.status === 'Fail' || attr.status === 'FAIL',
          );
          if (hasAttributeFailure) {
            console.log(`   ❌ Attribute comparison FAILED - changing result from PASS to FAIL`);
            sequentialResult = 'FAIL';
          } else {
            console.log(`   ✅ All attributes PASSED`);
          }
        }

        // Determine beacon type from event direction
        const beaconType = determineBeaconType(apiEvent.event_direction);

        processedBeacons.push({
          beaconID: apiEvent.event_name,
          index: index,
          timestamp: apiEvent.timestamp,
          beaconType: beaconType,
          deviceID: apiEvent.device_id,
          truth: truthData,
          beaconData: beaconData,
          flowComparison: flowComparison,
          eventName: apiEvent.event_name,
          stepName: matchResult.parentEvent ? matchResult.parentEvent.event_name : null,
          stepNumber: matchResult.parentEvent ? matchResult.parentEvent.event_order || null : null,
          actualSequence: undefined,
          hasOrderIssue: false, // Will be calculated in second pass
          consumedQueuePosition: matchResult.queueIndex,
          // sequentialValidation: sequentialResult,
          isBlockedEvent: apiEvent.is_blocked_event || false,
          blockedUserAttributes: apiEvent.blocked_user_attributes || null,
        });

        index++;
      });

      // Log unconsumed positions
      const unconsumedCount = expectedQueue.length - consumedPositions.size;
      console.log(
        `🔍 LOOKAHEAD: Processed all ${apiEvents.length} events. Consumed ${consumedPositions.size}/${expectedQueue.length} queue positions`,
      );

      if (unconsumedCount > 0) {
        console.log(`⚠️  MISSING ${unconsumedCount} expected scenarios that never arrived`);
      }

      // ========================================
      // PHASE 5: SECOND PASS - ORDER CALCULATION
      // ========================================

      calculateOrderIssues(processedBeacons, expectedQueue, consumedPositions);

      // ========================================
      // PHASE 6: CREATE MISSING EVENTS & FINAL ASSEMBLY
      // ========================================

      // Collect expected inbound events
      const expectedInboundEvents = [];
      flowData.events.forEach((event) => {
        event.scenarios
          .filter((scenario) => scenario.scenario_type === 'inbound')
          .forEach((scenario) => {
            expectedInboundEvents.push(scenario);
          });
      });

      // Categorize events using helper
      const {
        validatedInbound: validatedInboundEvents,
        validatedOutbound: validatedOutboundEvents,
        nonValidated: nonValidatedEvents,
      } = categorizeProcessedEvents(processedBeacons);

      // Sort ALL existing events by timestamp
      const allExistingEvents = [
        ...validatedInboundEvents,
        ...validatedOutboundEvents,
        ...nonValidatedEvents,
      ];
      allExistingEvents.sort((a, b) => a.timestamp - b.timestamp);

      // Sort ONLY validated INBOUND events to determine their actual sequence
      validatedInboundEvents.sort((a, b) => a.timestamp - b.timestamp);

      // Assign actual sequence numbers to validated INBOUND events
      validatedInboundEvents.forEach((event, idx) => {
        event.actualSequence = idx + 1; // 1-indexed arrival order
      });

      // Create missing events
      const missingEvents = createMissingInboundEvents(
        expectedInboundEvents,
        processedBeacons,
        flowData,
        deviceID,
        index,
        expectedQueue,
        consumedPositions,
      );

      // Build expected order for assembly using helper
      const sortedExpectedOrder = buildExpectedInboundOrder(flowData);

      // Assemble final list
      const finalList = assembleFinalEventList(
        allExistingEvents,
        missingEvents,
        expectedQueue,
        consumedPositions,
      );

      // Replace processedBeacons with the correctly ordered list
      processedBeacons.length = 0;
      processedBeacons.push(...finalList);

      // Check if any events have order issues
      const hasOrderIssue = processedBeacons.some((b) => b.hasOrderIssue);

      // Store order validation info
      if (hasOrderIssue) {
        processedBeacons.forEach((beacon) => {
          beacon.flowHasOrderIssue = hasOrderIssue;
        });
      }

      // ========================================
      // PHASE 7: DISPATCH RESULTS
      // ========================================

      dispatch(updateOutput(processedBeacons));
      dispatch(setRequestStatus(requestStatus));
      dispatch(setRequestMessage(requestMessage));
    } catch (error) {
      console.error('Error in updateAsync:', error);

      dispatch(setRequestStatus(requestStatus));
      dispatch(setRequestMessage(requestMessage || error.message));
      dispatch(setLoading(false));
    }
  };

// ========================================
// SELECTORS
// ========================================

export const outputValue = (state) => state.validationResults.value;
export const outputLoading = (state) => state.validationResults.loading;
export const outputResponseCode = (state) => state.validationResults.requestStatus;
export const outputResponseMessage = (state) => state.validationResults.requestMessage;
export const outputIncludeNonValidated = (state) => state.validationResults.includeNonValidated;
export const outputIncludeOutboundBeacons = (state) =>
  state.validationResults.includeOutboundBeacons;
export const selectValidationMetadata = (state) => state.validationResults.metadata;
export const selectAddedEvents = (state) => state.validationResults.addedEvents;

export default validationResultsSlice.reducer;
